import { createClient as createSupabaseClient } from "@supabase/supabase-js"

// Module-level singleton to prevent multiple instances even during rapid calls
let moduleClientInstance: ReturnType<typeof createSupabaseClient> | null = null

/**
 * Get consistent storage key based on Supabase project URL
 * This ensures all clients use the same storage key to prevent multiple instance warnings
 */
function getStorageKey(): string {
  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL || ''
  // Extract project ref from URL (e.g., "owjoefcnojwhghkhhrvj" from "https://owjoefcnojwhghkhhrvj.supabase.co")
  const match = supabaseUrl.match(/https?:\/\/([^.]+)\.supabase\.co/)
  const projectRef = match ? match[1] : 'default'
  return `sb-${projectRef}-auth-token`
}

/**
 * Global Supabase client singleton
 * Uses window object for true singleton across module boundaries
 * Uses consistent storage key to prevent "Multiple GoTrueClient instances" warnings
 * 
 * SSR-safe: returns a client that works during SSR (with no-op storage) and browser (with localStorage)
 */
function getClientInstance() {
  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL
  const supabaseAnonKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY
  
  if (!supabaseUrl || !supabaseAnonKey) {
    throw new Error('Missing Supabase environment variables')
  }

  // Use consistent storage key across all instances
  const storageKey = getStorageKey()

  // In browser: use window object for true singleton
  if (typeof window !== 'undefined') {
    // Check module-level singleton first (fastest, no DOM access)
    if (moduleClientInstance) {
      return moduleClientInstance
    }
    
    // Then check window-level singleton
    const globalKey = '__supabase_client_singleton'
    if ((window as any)[globalKey]) {
      moduleClientInstance = (window as any)[globalKey]
      return moduleClientInstance
    }
    
    // Create client with localStorage for browser
    // Using explicit storageKey ensures all instances share the same identity
    const client = createSupabaseClient(supabaseUrl, supabaseAnonKey, {
      auth: {
        storage: window.localStorage,
        storageKey: storageKey, // Explicit storage key for consistency
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
      },
    })

    // Store in both module-level and window-level for maximum singleton enforcement
    moduleClientInstance = client
    ;(window as any)[globalKey] = client
    return client
  }

  // During SSR/SSG: create client with same storage key but no-op storage
  // Using the same storageKey ensures Supabase recognizes this as the same client
  // even though storage is undefined (won't be used during SSR anyway)
  return createSupabaseClient(supabaseUrl, supabaseAnonKey, {
    auth: {
      storage: undefined, // No storage during SSR
      storageKey: storageKey, // Same storage key for consistency
      autoRefreshToken: false,
      persistSession: false,
      detectSessionInUrl: false,
    },
  })
}

/**
 * Client-side Supabase client creator
 * Returns a singleton instance to prevent multiple GoTrueClient instances
 * SSR-safe: works during server-side rendering for static generation
 */
export function createClient() {
  return getClientInstance()
}
